"use client"

import Link from "next/link"
import { Spotlight } from "@/components/ui/Spotlight"
import { TextGenerateEffect } from "@/components/ui/TextGenerateEffect"
import { ArrowRight } from "lucide-react"
import { useTranslations } from "next-intl"

export function HeroSection() {
  const t = useTranslations("hero")
  return (
    <section className="relative w-full min-h-screen flex flex-col justify-center pb-20 pt-36">
      {/* Spotlights - positioned for full-width effect */}
      <div className="absolute inset-0 overflow-hidden">
        <Spotlight
          className="-top-40 -left-10 md:-left-32 md:-top-20 h-screen"
          fill="white"
        />
        <Spotlight
          className="h-[80vh] w-[50vw] top-10 left-[60%]"
          fill="hsl(168 50% 44%)"
        />
        <Spotlight 
          className="left-[30%] top-28 h-[80vh] w-[50vw]" 
          fill="hsl(38 75% 55%)"
        />
      </div>

      {/* Background grid - full width */}
      <div
        className="absolute inset-0 w-full h-full dark:bg-background bg-white dark:bg-grid-white/[0.02] bg-grid-black/[0.2]"
      >
        <div
          className="absolute pointer-events-none inset-0 dark:bg-background
          bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]"
        />
      </div>

      {/* Content - centered with max-width */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex flex-col items-center justify-center">
          
          {/* Tag line */}
          <p className="uppercase tracking-widest text-xs text-center text-accent-primary max-w-96 mb-4">
            {t("tagline")}
          </p>

          {/* Main headline */}
          <TextGenerateEffect
            words={t("title")}
            className="text-center text-[40px] md:text-5xl lg:text-6xl font-bold"
          />

          {/* Philosophy statement */}
          <p className="text-center text-muted-foreground mt-6 mb-8 text-lg md:text-xl lg:text-2xl max-w-3xl leading-relaxed">
            {t("philosophy")}{" "}
            <span className="text-foreground font-medium">{t("versionable")}</span>,{" "}
            <span className="text-foreground font-medium">{t("scalable")}</span>, {t("and")}{" "}
            <span className="text-foreground font-medium">{t("shareable")}</span>
            {t("philosophySuffix")}
          </p>

          {/* Subtext */}
          <p className="text-center text-muted-foreground/80 text-sm md:text-base max-w-2xl mb-10">
            {t("subtitle")}
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-center mb-20">
            <Link
              href="/tech-lab"
              className="group inline-flex items-center justify-center gap-2 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-background transition-all hover:bg-accent-primary/90 hover:gap-3"
            >
              <span className="w-2 h-2 rounded-full bg-teal-500/80" />
              {t("cta.techLab")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>

            <Link
              href="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card/50 px-6 py-3 text-sm font-medium text-foreground transition-all hover:bg-card hover:border-accent-primary/50"
            >
              {t("cta.contact")}
            </Link>
          </div>
          
          {/* Scroll indicator - positioned below CTAs */}
          <div className="flex flex-col items-center gap-2 text-muted-foreground/60">
            <span className="text-xs uppercase tracking-widest">{t("scroll")}</span>
            <div className="w-px h-8 bg-gradient-to-b from-muted-foreground/40 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  )
}
